<template>
    <div class="sidebar" ref="sidebar">
        <div 
            class="editable-box" 
            contenteditable="true" 
            ref="editableBox" 
            @focus="startEditing" 
            @blur="saveContent"
            @input="applyStyling"
            @compositionstart="isComposing = true" 
            @compositionend="handleCompositionEnd"
            @keydown="handleKeydown"
        >
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { rightHandFingeringMapping, leftHandFingeringMapping, complexFingeringMapping } from "../assets/NoteMapping";
import { ScoreCache } from '../store/cache';

export default {
    name: "SideBar",
    data() {
        return {
            isEditing: false,
            isComposing: false,
            enterPressed: false,
//             content: `= Header Section 描述头定义
// B: Usage Example
// D: F

// = Notes 谱子
// 大62抹七 进56/ 复62/ | 大7注挑七 猱- | 挑七急撞/. 名7韬起// 绰名76挑六/ 散挑五 带起/ | 大79剔六猱. 散五如一. | 散勾二/ 名10挑五猱.  | 大85掩五/ 名10韬起/ | 
// 名10挑七// 进9// 复10// 名10挑六// 勾五// 急退12// 复10/ | 133勾五/ 六/ 大9挑七/ 六// 爪起// 散勾五 | 大7抹七/ 挑七// 带起// 中7勾二 | 名76勾五放合 进7/ 进62// 带起// | 
// 大7抹五/. 进62// 62挑五// 退7// 名76韬起// 进7// 散勾三 | 撮大62七散三 撮大56七散四/ 进5// 进44// 撮大57七散五/ 退56/ | 56剔五急撞/. 名76韬起// 名76勾四/ 退9// 复76// |
// 大79剔六猱/ 散五如一. | 登大76七散六// 爪起// 撮名9七散五// 带起// 登大76七散六// 爪起// 撮名9七散五// 带起// 登大76七散六 | 名10挑六. 绰名10历七六/ |

// = Lyrics 歌词
// 小 燕 子 穿 花 衣， - 年 年 春 天 来 这 里， - 我 问 燕 子 你 为 啥 来， - 燕 子 说， - 这 里 的 春 天 最 美 丽。`
            content: `= Header Section 描述头定义
B: 阳关三叠（节选）
D: bB
Z: 据《琴学入门》（1864年）查阜西教授

= Notes 谱子
散勾三/. 勾四// 勾五/ 抹六/ 挑六- | 勾四/. 勾五// 挑七/ 挑六/ | 勾五 挑六 名10摘四- | 大9抹五/ 进79/ 大9挑五 勾四/ 进76/ 79剔四/ 退9// 名10韬起// | 散挑五 名10勾四/ 上9/ 名10剔四散三如一- | 散挑五/. 挑四// 名10勾二/ 散挑四/ | 挑三 名10勾二 散挑四- | 勾四/. 勾五// 挑七/ 挑六/ | 勾五 挑六 名10摘四- |

= Lyrics 歌词
清 和 节 当 春， 渭 城 朝 雨 邑 轻 尘， 客 - 舍 青 - 青 - - 柳 色 - 新； 劝 君 更 尽 一 杯 酒， 西 出 阳 关 无 故 人。`
        };
    },
    watch: {
        settings: {
            handler() {
                this.parseInput(this.content);
                this.barCount = -1;
            },
            deep: true,
            immediate: true, 
        },
        currentMode: {
            handler() {
                this.parseInput(this.content);
            },
            immediate: true
        },
        manualLayout: {
            handler() {
                this.parseInput(this.content);
            },
            immediate: true
        }
    },
    computed: {
        ...mapGetters(["settings", "svgList", "currentMode", "manualLayout"]), 
    },
    mounted() {
        this.$refs.editableBox.innerHTML = this.applyStylingToText(this.content);
        this.addStyles();

        // 尝试恢复缓存的状态
        const cached = ScoreCache.loadState();
        if (cached) {
            this.setContent(cached.content);
            this.$store.dispatch('setSettings', cached.settings);
        }

        // 定期保存状态
        this.autoSaveInterval = setInterval(() => {
            ScoreCache.saveState(this.content, this.$store.state.settings);
            this.parseInput(this.content);
        }, 500); // 每1.5秒保存一次
    },
    beforeDestroy() {
        // 清理定时器
        if (this.autoSaveInterval) {
            clearInterval(this.autoSaveInterval);
        }
    },
    methods: {
        ...mapActions(["upsertSvgElement", "updateBarCount", "setTuningNotes"]), 

        addStyles() {
            const style = document.createElement('style');
            style.textContent = `
                .dynamic-comment {
                    color: #7b7b7b;
                }
                .dynamic-special {
                    color: #007bff;
                }
            `;
            document.head.appendChild(style);
        },
        startEditing() {
            this.isEditing = true;
        },
        saveContent() {
            this.content = this.$refs.editableBox.innerText.trim();
            this.isEditing = false;

            // parsing
            this.parseInput(this.content);
        },
        handleCompositionEnd() {
            this.isComposing = false;
            this.applyStyling();
        },
        handleKeydown(event) {
            if (event.key === '=') {
                event.preventDefault();
                alert('不能输入 = 符号');
                return;
            }
            
            if (event.key === "Enter") {
                this.enterPressed = true;
            }

            // 获取选中的文本
            const selection = window.getSelection();
            const selectedText = selection.toString();

            // 定义关键标记及其组成部分
            const criticalMarkers = {
                "= 以下为描述头定义": ["=", "以下", "为", "描述", "头", "定义"],
                "B:": ["B", ":"],
                "D:": ["D", ":"],
                "Z:": ["Z", ":"],
                "= 以下为谱子主体": ["=", "以下", "为", "谱子", "主体"],
                "= 以下为歌词": ["=", "以下", "为", "歌词"]
            };

            // 检查是否选中了关键标记的任何部分
            for (const [marker, parts] of Object.entries(criticalMarkers)) {
                // 检查完整标记
                if (selectedText.includes(marker)) {
                    if (event.key === "Backspace" || event.key === "Delete") {
                        event.preventDefault();
                        alert(`不能删除关键标记："${marker}"`);
                        return false;
                    }
                }
                
                // 检查部分标记
                for (const part of parts) {
                    // 获取当前光标位置的上下文
                    const cursorPosition = selection.anchorOffset;
                    const containerText = selection.anchorNode?.textContent || '';
                    
                    // 检查选中文本是否包含部分标记
                    if (selectedText.includes(part)) {
                        // 检查这部分文本是否属于完整的关键标记
                        const surroundingText = containerText.substring(
                            Math.max(0, cursorPosition - marker.length),
                            Math.min(containerText.length, cursorPosition + marker.length)
                        );
                        
                        if (marker.includes(part) && surroundingText.includes(part)) {
                            if (event.key === "Backspace" || event.key === "Delete") {
                                event.preventDefault();
                                alert(`不能删除关键标记"${marker}"的任何部分`);
                                return false;
                            }
                        }
                    }
                }
            }
        },
        applyStyling() {
            if (this.isComposing) return;

            const { cursorPosition, prevCharIsNewline } = this.getCaretPosition();

            this.content = this.$refs.editableBox.innerText; 
            this.$refs.editableBox.innerHTML = this.applyStylingToText(this.content);

            const adjustedPosition = this.enterPressed ? cursorPosition + 1 : cursorPosition;
            this.setCaretPosition(adjustedPosition, prevCharIsNewline);

            this.enterPressed = false;
        },
        applyStylingToText(text) {
            // 1. 首先处理注释
            let result = text.replace(/(=.*$)/gm, '<span class="dynamic-comment">$1</span>');
            
            // 2. 处理描述头标记 (B:, D:, Z:)
            result = result.replace(/^([BDZ]:)/gm, '<span class="dynamic-special">$1</span>');
            
            // 3. 处理斜杠 (排除数字之间的斜杠)
            result = result.replace(/(?<!\d)\/(?![\ds])|(?<=\d)\/(?![\ds])|(?<!\d)\/(?=\d)/g, '<span class="dynamic-special">/</span>');
            
            // 4. 处理其他特殊字符
            result = result.replace(/(\||--|(?<![a-zA-Z])-(?![a-zA-Z])|\.|\/\/)/g, '<span class="dynamic-special">$1</span>');
            
            return result;
        },
        getCaretPosition() {
            const selection = window.getSelection();
            if (selection.rangeCount === 0) return { cursorPosition: 0, prevCharIsNewline: false };

            const range = selection.getRangeAt(0);
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(this.$refs.editableBox);
            preCaretRange.setEnd(range.endContainer, range.endOffset);

            const position = preCaretRange.toString().length;
            const prevChar = preCaretRange.toString().slice(-1);
            const prevCharIsNewline = prevChar === '\n';

            return { cursorPosition: position, prevCharIsNewline };
        },
        setCaretPosition(position, prevCharIsNewline) {
            const el = this.$refs.editableBox;
            const range = document.createRange();
            const sel = window.getSelection();
            let charCount = 0, found = false;

            function traverseNodes(node) {
                if (found) return;

                if (node.nodeType === Node.TEXT_NODE) {
                    const nextCharCount = charCount + node.length;
                    if (position <= nextCharCount) {
                        const offset = prevCharIsNewline ? position - charCount + 1 : position - charCount;
                        range.setStart(node, offset);
                        range.collapse(true);
                        found = true;
                    }
                    charCount = nextCharCount;
                } else {
                    for (let i = 0; i < node.childNodes.length; i++) {
                        traverseNodes(node.childNodes[i]);
                        if (found) break;
                    }
                }
            }

            traverseNodes(el);
            sel.removeAllRanges();
            sel.addRange(range);
        },

        convertChineseToNumber(chineseNumber) {
            const map = {
                一: 1,
                二: 2,
                三: 3,
                四: 4,
                五: 5,
                六: 6,
                七: 7,
            };
            return map[chineseNumber] || null;
        },

        parseNotes(input) {
            this.$store.state.noteList = []
            this.$store.state.lineList = []

            // 按 # 分割文本，获取所有部分
            const sections = input.split('=').filter(Boolean);
            if (sections.length < 3) {
                alert('缺少必要的分节标记');
                return [];
            }

            // const mainSectionMatch = input.match(/# 以下为谱子主体([\s\S]*?)(?=# 以下为歌词)/);
            const notesInput = sections[1].split('\n').slice(1).join('\n').trim();
            if (!notesInput) return [];

            // 获取歌词部分并按空格拆分
            // const lyricsMatch = input.match(/# 以下为歌词\n([\s\S]*)/);
            const lyrics = sections[2].split('\n').slice(1).join('\n').trim().split(/\s+/);

            const noteParts = notesInput.split(/(\n)|([^\S\n]+)/).filter(p => p && p !== ' ');
            let remainingBeats = this.currentMode === 'piano-roll' ? this.settings.beatNum : this.settings.noteNum; // 当前小节剩余节拍数
            let barIndex = 1;
            let beatIndex = 0;
            let lastStringNo = null;
            let lastHuiNo = null;
            let lyricIndex = 0;

            // console.log(this.manualLayout)

            for (const part of noteParts) {

                // 处理手动换行符
                if (this.manualLayout && part === '\n') {
                    barIndex += 1;
                    beatIndex = 0;
                    continue;
                }

                // 处理小节线
                if (part === "|") {
                    if (this.currentMode === 'guitar-tablature') {
                        remainingBeats -= 1;  // 占用一个grid
                        beatIndex += 1;       // 移动到下一个grid
                    }
                    this.$store.state.lineList.push({
                        id: "bar-line",
                        barIndex: barIndex,
                        beatIndex: remainingBeats < 1 && this.currentMode === 'guitar-tablature' ? beatIndex - 0.5 : beatIndex
                    });
                    continue; // 跳过时值计算
                }

            
                const symbols = part.match(/[-./]+/g) || [];
                let text = part.replace(/[-./]+/g, "");

                // 1. 解析音符时值
                let duration = 1;
                for (const symbol of symbols) {
                    for (const char of symbol.split('')) {
                        if (char === "-") duration += 1; // 延音
                        if (char === "/") duration /= 2; // 减半
                        if (char === ".") duration += duration / 2; // 附点
                    }
                }
                
                // 2. 自动换行模式检查是否需要换到下一个rollBar
                if (this.currentMode === 'piano-roll' && duration > remainingBeats && !this.manualLayout) {
                    barIndex += 1;
                    beatIndex = 0;
                    remainingBeats = this.settings.beatNum;
                } else if (this.currentMode === 'guitar-tablature' && remainingBeats < 1 && !this.manualLayout) {
                    barIndex += 1;
                    beatIndex = 0;
                    remainingBeats = this.settings.noteNum;
                }

                // 插播：处理休止符
                if (text === '0') {
                    this.$store.state.noteList.push({
                        id: "note",
                        duration,  // 保留时值的解析
                        barIndex,
                        beatIndex,
                        stop: true,
                        lyric: lyrics[lyricIndex] === '-' ? null : lyrics[lyricIndex]
                    });
                    if (this.currentMode === 'piano-roll') {
                        remainingBeats -= duration;
                        beatIndex += duration;
                    } else {
                        remainingBeats -= 1;
                        beatIndex += 1;
                    }
                    lyricIndex++; 
                    continue;
                }

                // 3. 解析指法
                let complexFingering = null;
                for (const key of Object.keys(complexFingeringMapping)) {
                    if (text.includes(key)) {
                        complexFingering = complexFingeringMapping[key];
                        text = text.replace(key, "").trim();
                        break;
                    }
                }
                let iter = complexFingering ? complexFingering.parts : 1;
                let splitDuration = complexFingering ? complexFingering.splitDuration : false;
                let direction = complexFingering ? complexFingering.directions : null;
                let finger = complexFingering ? complexFingering.finger : null;
                let addString = complexFingering ? complexFingering.addString : false;
                
                if (splitDuration) duration /= 2;

                for (let i = 0; i < iter; i++) {

                    // 自动换行模式检查复杂指法是否需要换行
                    if (this.currentMode === 'piano-roll' && duration > remainingBeats && !this.manualLayout) {
                        barIndex += 1;
                        beatIndex = 0;
                        remainingBeats = this.settings.beatNum;
                    } else if (this.currentMode === 'guitar-tablature' && remainingBeats < 1 && !this.manualLayout) {
                        barIndex += 1;
                        beatIndex = 0;
                        remainingBeats = this.settings.noteNum;
                    }

                    // 3.1 提取弦号和徽位
                    const stringNoMatch = text.match(/[一二三四五六七]/); 
                    const huiNoMatch = text.match(/(?:散|\d+)(?:'\d+)?/);

                    const stringNo = stringNoMatch
                        ? this.convertChineseToNumber(stringNoMatch[0])
                        : lastStringNo;

                    const huiNo = (() => {
                        // 如果没有匹配到徽数
                        if (!huiNoMatch) {
                            if (this.currentMode === 'guitar-tablature' && lastHuiNo !== null) 
                                return text.includes('起') || text.includes("放合")? lastHuiNo + '↑' : lastHuiNo;
                            return '-';
                        }
                        const hui = huiNoMatch[0];
                        text = text.replace(hui, "").trim();

                        if (hui === "散") lastHuiNo = 0;
                        if (hui === "卜") lastHuiNo = "13'3";
                        
                        if (/^\d+'?\d*$/.test(hui)) {
                            if (hui.includes("'")) lastHuiNo = hui;
                            else if (hui.length === 2) {
                                const num = parseInt(hui, 10);
                                if (num >= 10 && num <= 13) lastHuiNo = hui;
                                else lastHuiNo = `${hui[0]}'${hui[1]}`;
                            }
                            else if (hui.length === 3) lastHuiNo = `${hui.slice(0, -1)}'${hui.slice(-1)}`;
                            else lastHuiNo = hui;
                        }

                        if (this.currentMode === 'guitar-tablature' && (text.includes('起') || text.includes("放合"))) 
                            lastHuiNo += '↑';

                        return lastHuiNo;
                    })();
                        
                    if (stringNoMatch) {
                        lastStringNo = stringNo;
                        if (addString) {
                            const characters = ['一', '二', '三', '四', '五', '六'];
                            text = text.replace(stringNoMatch[0], characters[stringNo - 2]).trim();
                            text += "散";
                        }
                        else text = text.replace(stringNoMatch[0], "").trim();
                    }

                    // 3.2 解析指法
                    const rightFingering = Object.keys(rightHandFingeringMapping).find(key => {
                        if (text.includes(key)) {
                            text = text.replace(key, "").trim(); // 删除匹配到的右手指法
                            return true;
                        }
                        return false;
                    });
                    const rightHand = rightHandFingeringMapping[rightFingering] || {};

                    const specialRightHand = text.includes("掩") ? "打" : null;
                    text = text.replace("掩", "").trim();

                    let leftHandMapping = null;
                    for (const key of Object.keys(leftHandFingeringMapping).sort((a, b) => b.length - a.length)) {
                        if (text.includes(key)) {
                            leftHandMapping = leftHandFingeringMapping[key];
                            text = text.replace(key, "").trim();
                            break;
                        }
                    }

                    // 检查剩余的text中是否包含左手指法
                    const leftHandFinger = text.match(/[大人中名]/)?.[0] || null;
                    text = text.replace(/[大人中名]/, '').trim();

                    // 3.3 生成音符对象
                    this.$store.state.noteList.push({
                        id: "note",
                        huiNo,
                        stringNo,
                        duration,
                        barIndex,
                        beatIndex,
                        rightHandDirection: direction ? direction[i] : rightHand.direction || null,
                        rightHandFinger: finger ? finger[i] : rightHand.finger || null,
                        leftHandSvg: leftHandMapping?.svgName || null,
                        leftHandSvgPos: leftHandMapping?.position || null,
                        specialRightHand,
                        leftHandFinger,
                        lyric: lyrics[lyricIndex] === '-' ? null : lyrics[lyricIndex]
                    });

                    // 3.4 更新数据
                    if (splitDuration && i == 0) {
                        if (this.currentMode === 'piano-roll') {
                            remainingBeats -= duration;
                            beatIndex += duration;
                        } else {
                            remainingBeats -= 1;
                            beatIndex += 1;
                        }
                    } 
                }

                // 4. 更新数据
                if (this.currentMode === 'piano-roll') {
                    remainingBeats -= duration;
                    beatIndex += duration;
                } else {
                    remainingBeats -= 1;
                    beatIndex += 1;
                }
                lyricIndex++; 
            }
            
            // console.log(this.$store.state.lineList)
            // console.log(this.$store.state.noteList)

            return barIndex;
        },

        parseInput(input) {
            const svgElements = [];

            // title
            const titleMatch = input.match(/^B:\s*(.+)$/m);
            const titleText = titleMatch ? titleMatch[1].trim() : "Untitled";
            svgElements.push({
                id: "title",
                type: "text",
                attrs: {
                    x: "50%",
                    // y: this.settings.margin.top,
                    // fontSize: this.settings.fontSize.title,
                    text: titleText,
                    // anchor: "middle",
                    fill: this.settings.titleColor,
                    fontWeight: this.settings.titleBold ? 'bold' : 'normal',
                    fontStyle: this.settings.titleItalic ? "italic" : "normal",
                    fontFamily: this.settings.titleFont,
                },
            });

            // key
            const keyMatch = input.match(/^D:\s*(.+)$/m);
            const keyText = keyMatch ? keyMatch[1].trim() : "F";
            const keyParts = keyText.split(/\s+/); // 按空格分割

            if (keyParts.length === 1) {
                // 单个调号，使用标准定调
                this.setTuningNotes({ type: 'standard', key: keyParts[0] });
            } else if (keyParts.length === 7) {
                // 7个音符，直接作为定调
                this.setTuningNotes({ type: 'custom', notes: keyParts });
            } else {
                // 数量不对，报错
                alert('调号格式错误：需要输入1个调号或7个音符');
                return;
            }
            svgElements.push({
                id: "key",
                type: "text",
                attrs: {
                    x: this.settings.margin.left,
                    y: this.settings.margin.top,
                    // fontSize: this.settings.fontSize.fingerings,
                    text: keyParts.length === 1 ? "1=" + keyText : "",
                    fill: this.settings.fingeringsColor,
                    fontWeight:  this.settings.fingeringsBold ? 'bold' : 'normal',
                    fontStyle: this.settings.fingeringsItalic ? "italic" : "normal",
                    fontFamily: this.settings.fingeringsFont,
                },
            });

            // author
            const authorMatch = input.match(/^Z:\s*(.+)$/m);
            const authorText = authorMatch ? authorMatch[1].trim() : "Undefined";
            svgElements.push({
                id: "author",
                type: "text",
                attrs: {
                    // x: "50%",
                    // y: this.settings.margin.top,
                    // fontSize: this.settings.fontSize.title,
                    text: authorText,
                    // anchor: "middle",
                    fill: this.settings.fingeringsColor,
                    fontWeight:  this.settings.fingeringsBold ? 'bold' : 'normal',
                    fontStyle: this.settings.fingeringsItalic ? "italic" : "normal",
                    fontFamily: this.settings.fingeringsFont,
                },
            });

            
            // bar
            const barCount = (() => {
                // 找到 "# 以下为谱子主体" 和 "# 以下为歌词" 的位置
                const mainSectionIndex = input.indexOf("= 以下为谱子主体");
                const lyricsIndex = input.indexOf("= 以下为歌词");
                
                if (mainSectionIndex === -1) return 0; // 如果找不到谱子主体，返回 0

                // 截取从谱子主体开始到歌词部分（如果有）之前的内容
                const endIndex = lyricsIndex !== -1 ? lyricsIndex : input.length;
                const mainSectionContent = input.slice(mainSectionIndex, endIndex);

                // 统计换行的次数（剔除空行）
                return mainSectionContent.split('\n').filter(line => line.trim() !== '').length - 2;
            })();
            this.updateBarCount(barCount);


            // notes
            let newBarCount = this.parseNotes(input);
            if (newBarCount > barCount) this.updateBarCount(newBarCount);
            // console.log(notesInput)

            // svgList
            svgElements.forEach((element) => {
                this.upsertSvgElement(element);
                // console.log(element)
            });
        },

        exportTxt() {
            try {
                if (!this.$refs.editableBox) {
                    throw new Error("EditableBox reference not found");
                }
                
                const content = this.$refs.editableBox.innerText;

                const blob = new Blob([content], { type: 'text/plain' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'export.txt';
                
                document.body.appendChild(a);
                a.click();
                
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                console.log("Export completed successfully");
            } catch (error) {
                console.error("Error in exportTxt:", error);
            }
        },

        // 设置文本内容的方法
        setContent(text) {
            if (this.$refs.editableBox) {
                this.$refs.editableBox.innerText = text;
                this.applyStyling();
            } else {
                console.error("SideBar: EditableBox reference not found");
            }
        }

    }
};
</script>

<style>
.sidebar {
    height: auto;
    min-height: 100%;
    margin: 15px;
    background-color: #fcfafa;
    border-radius: 5px;
    padding: 10px;
    box-sizing: border-box;
}

.editable-box {
    width: 100%;
    min-height: 100%;
    outline: none;
    cursor: text;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: small;
}

.dynamic-comment {
    color: #7b7b7b;
    user-select: none;  /* 使标记难以选中 */
    pointer-events: none;  /* 禁用鼠标事件 */
    position: relative;  /* 用于定位伪元素 */
}

.dynamic-special {
    color: #007bff;
    user-select: none;
    pointer-events: none;
    position: relative;
}

/* 为关键标记添加透明覆盖层 */
.dynamic-comment::after,
.dynamic-special::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
}
</style>